import React, { forwardRef, useImperativeHandle, useRef } from "react";
import defaultSgtradexLogo from "@/assets/svgs/logo-sgtradex.svg";
import { DocumentDetailsSection } from "../../DocumentDetailsSection";
import { EndorsementChainSection } from "../../EndorsementChainSection";
import { ProcessPDF, convertSvgUrlToBase64 } from "@/utils/pdf-download";
import { pdfHeader } from "@/utils/pdfHeader";
import PropTypes from 'prop-types';

const smallText = (text) => <p style={{ wordBreak: "break-word", fontSize: "12px", fontWeight: 300 }}>{text}</p>;
const smallSemiBoldText = (text) => <p style={{ fontSize: "12px", fontWeight: 600 }}>{text}</p>;
const smallStrongText = (text) => <p style={{ fontSize: "12px", fontWeight: 700 }}>{text}</p>;
const SubTitle1 = ({ title }) => <p style={{ fontSize: '1.125rem', padding: '0.5rem', fontWeight: 800 }}>{title}</p>;
const SubTitle2 = ({ title }) => <p style={{ fontSize: '1rem', padding: '0.5rem', fontWeight: 800 }}>{title}</p>;
const SubTitle3 = ({ title, italic }) => <p style={{ fontSize: '0.875rem', padding: '0.5rem', fontStyle: italic ? 'italic' : 'normal', fontWeight: 800 }}>{title}</p>;
const parsedValue = (value) => value !== null && value !== undefined ? `${value}` : '';

const labelValueDivider = () => <div style={{
  marginTop: '0.5rem',
  marginBottom: '0.5rem',
  borderTop: "1px solid black",
  width: '1rem'
}}></div>

const SingleValueField = ({ identifier, schema, payload, index }) => {
  return <div style={{
    flex: 1,
    fontSize: '0.75rem',
    borderTop: "1px solid black",
    borderRight: "1px solid black",
  }} key={`document-row-${index}`}>
    <div style={{ padding: '0.5rem' }}>
      {smallSemiBoldText(schema?.[identifier]?.title)}
      {labelValueDivider()}
      {smallText(parsedValue(payload?.[identifier]))}
    </div>
  </div>
}
const Row = ({ children, escapePrintBottomBorder = false }) => {
  return <div style={{
    display: 'flex',
    breakInside: 'avoid',
    ...(escapePrintBottomBorder ? {} : {
      borderColor: 'var(--border)',
      borderBottomWidth: '1px',
      marginBottom: '-1px'
    })
  }}>
    {children}
  </div>
}

const MultiValueField = ({ identifier, schema, payload, index, escapePrintBottomBorder = false }) => {
  return <div style={{
    flex: 'auto',
    borderTop: "1px solid black",
    borderRight: "1px solid black",
    height: '100%',
    // ...(only ? { height: '100%', borderRight: "1px solid" } : {}),
    // ...(last ? { borderRightWidth: '0px' } : {}),
    breakInside: 'avoid',
    ...(escapePrintBottomBorder ? {} : {
      borderBottomWidth: '1px',
    })
  }}>
    <div style={{ padding: '0.5rem' }}>
      {smallSemiBoldText(schema?.[identifier]?.title)}
      {labelValueDivider()}
      <div>
        {(payload)[identifier]?.map((value, index) => (
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
          }} key={`document-row-${index}`}>
            <li />
            {smallText(parsedValue(value))}
          </div>
        ))}
      </div>
    </div>
  </div>
}

const MultiObjectField = ({
  identifier,
  schema,
  payload,
  arrayKeys = [Object.keys(payload)],
  title = schema?.[identifier]?.title,
  escapePrintBottomBorder = false
}) => {
  // Default payload to single object array
  if (!Array.isArray(payload) && payload) {
    payload = [payload];
  }
  if (payload?.length === 0 || !payload) {
    payload = [[]];
    let defaultObj = {}
    arrayKeys.flat().map((key) => defaultObj[key] = null);
    payload[0].push(defaultObj);
  }

  return <div style={{
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid black',
  }}>
    <div style={{ breakAfter: 'avoid' }}>
      {typeof title === 'string' && title?.length ?
        <div style={{ padding: '0.5rem' }}>
          {smallStrongText(`${title}`)}
        </div>
        : title
      }
    </div>
    {arrayKeys?.map((keys) => {
      return <>
        {payload?.map((payloadItem, index) => {
          return <div style={{
            display: "flex",
            flexGrow: 1,
            breakInside: "avoid",
            key: `document-row-${index}`,
            borderColor: "black",
            borderBottom: escapePrintBottomBorder ? "none" : "1px solid black",
            marginBottom: escapePrintBottomBorder ? "0" : "-1px"
          }} key={`document-row-${index}`}>
            {keys?.map((item, index) => (
              <SingleValueField
                identifier={item}
                schema={schema?.[identifier].items.properties}
                payload={payloadItem} />
            ))}
          </div>
        })}
      </>
    })}
  </div>
}

const Spacer = () => <div style={{
  height: '1rem',
  borderTop: "1px solid black",
  backgroundColor: '#f3f4f6'
}} />;

const indentSize = 4;
const Indent = () => <div style={{
  borderRight: "1px solid black",
  backgroundColor: '#f3f4f6',
  width: `${indentSize}px`
}} />;

const IndentedTable = ({ children }) => {
  return <div style={{
    display: 'flex',
    borderTop: "1px solid black"
  }}>
    <Indent />
    <div
      style={{ width: `calc(100% - ${indentSize}px)` }}
    >
      {children}
    </div>
  </div>
};

const Section3 = (document, elementSchema) => {
  const { dcsa_ebl_document } = document ?? { dcsa_ebl_document: [] };
  return <>
    {(dcsa_ebl_document ?? [{}])?.map(({
      issuing_party,
      transports,
      charges,
      issue_place,
      invoice_payable_at,
      contact_details,
      consignment,
      utilized_transport_equipment,
      references,
      customs_references,
      document_party,
      ...document
    }, index) => (
      <div style={{
        borderWidth: '2px',
        marginBottom: '2rem',
        border: "1px solid black"
      }}>
        <MultiObjectField
          identifier="dcsa_ebl_document"
          schema={elementSchema?.properties}
          payload={document}
          arrayKeys={[['bol_ref', 'shipping_ref', 'status', 'shipped_onboard', 'freight_payment_cd', 'origin_charges_cd'], ['destination_charges_cd', 'to_order', 'shipped_onboard_date', 'terms_and_conditions', 'origin_receipt_type', 'destination_delivery_type'], ['origin_cargo_movement_type', 'destination_cargo_movement_type', 'issue_date', 'received_shipment_date', 'service_contract_ref', 'contract_quotation_ref'], ['declared_value', 'declared_currency', 'carrier_cd', 'carrier_cd_provider', 'carrier_clause']]}
          title={<SubTitle1 title={`Document #${index + 1}`} />}
        />

        <Row>
          {['place_of_receipt', 'port_of_load', 'port_of_discharge', 'place_of_delivery']?.map((item, index) => (
            <MultiValueField identifier={item}
              schema={elementSchema?.properties
                .dcsa_ebl_document.items.properties}
              payload={document} />
          ))}
        </Row>

        {/* Issuing Party */}
        <Spacer />
        <div style={{ flex: "1 1 auto", borderTop: "1px solid black" }}>
          {(issuing_party ?? [{}])?.map((issuing_party_item, index) => (
            <MultiObjectField
              key={`document-row-${index}`}
              identifier="issuing_party"
              schema={elementSchema?.properties.dcsa_ebl_document.items.properties}
              payload={issuing_party_item}
              arrayKeys={[
                ['name', 'address', 'country', 'contact_nm', 'contact_type', 'contact_details'],
                ['identifying_code', 'code_provider', 'code_name', 'tax_ref_type', 'tax_country_cd', 'tax_ref']
              ]}
              title={
                <SubTitle2
                  title={`${elementSchema?.properties.dcsa_ebl_document.items.properties.issuing_party.title} #${index + 1}`}
                />
              }
            />
          ))}
        </div>

        {/* Transports Details */}
        <Spacer />
        <div style={{ flex: "1 1 auto", borderTop: "1px solid black" }}>
          {(transports ?? [{}])?.map((transports_item, index) => (
            <MultiObjectField
              key={`document-row-${index}`}
              identifier="transports"
              schema={elementSchema?.properties.dcsa_ebl_document.items.properties}
              payload={transports_item}
              arrayKeys={[
                ['planned_arrival_date', 'planned_departure_date', 'pre_carriage', 'on_carriage', 'receipt_location_nm', 'receipt_location_type'],
                ['receipt_unlocation_cd', 'receipt_facility_cd', 'receipt_facility_cd_provider', 'pol_location_nm', 'pol_location_type', 'pol_unlocation_cd'],
                ['delivery_location_nm', 'delivery_location_type', 'delivery_unlocation_cd', 'delivery_facility_cd', 'delivery_facility_cd_provider', 'pod_location_nm'],
                ['pod_location_type', 'pod_unlocation_cd', 'routing_location_nm', 'routing_location_type', 'routing_unlocation_cd', 'routing_facility_cd'],
                ['routing_facility_cd_provider', 'vessel_nm', 'voy_no', 'universal_voy_no']
              ]}
              title={
                <SubTitle2
                  title={`${elementSchema?.properties.dcsa_ebl_document.items.properties.transports.title} #${index + 1}`}
                />
              }
            />
          ))}
        </div>

        {/* Charges Details */}
        <Spacer />
        <div style={{ flex: "1 1 auto", borderTop: "1px solid black" }}>
          {(charges ?? [{}])?.map((charges_item, index) => (
            <MultiObjectField
              key={`document-row-${index}`}
              identifier="charges"
              schema={elementSchema?.properties.dcsa_ebl_document.items.properties}
              payload={charges_item}
              arrayKeys={[
                ['name', 'currency_amount', 'currency_cd', 'payment_term', 'calculation_basis', 'unit_price', 'quantity']
              ]}
              title={
                <SubTitle2
                  title={`${elementSchema?.properties.dcsa_ebl_document.items.properties.charges.title} #${index + 1}`}
                />
              }
            />
          ))}
        </div>

        {/* Place of Issue / Invoice Payable Location / Party Contact Details */}
        <Spacer />

        {/* Place of Issue */}
        <MultiObjectField
          style={{ flex: 1, display: 'flex', flexDirection: 'column', borderTop: "1px solid black" }}
          identifier="issue_place"
          schema={elementSchema?.properties
            .dcsa_ebl_document.items.properties}
          payload={issue_place}
          arrayKeys={[['location_nm', 'location_type', 'unlocation_cd']]}
          title={`${elementSchema?.properties
            .dcsa_ebl_document.items.properties
            .issue_place.title}`}
        />

        {/* Invoice Payable Location */}
        <MultiObjectField
          style={{ flex: 1, display: 'flex', flexDirection: 'column', borderTop: "1px solid black" }}
          identifier="invoice_payable_at"
          schema={elementSchema?.properties
            .dcsa_ebl_document.items.properties}
          payload={invoice_payable_at}
          arrayKeys={[['location_nm', 'location_type', 'unlocation_cd']]}
          title={`${elementSchema?.properties
            .dcsa_ebl_document.items.properties
            .invoice_payable_at.title}`}
        />

        {/* Party Contact Details */}
        <MultiObjectField
          style={{ flex: 1, display: 'flex', flexDirection: 'column', borderTop: "1px solid black" }}
          identifier="contact_details"
          schema={elementSchema?.properties
            .dcsa_ebl_document.items.properties}
          payload={contact_details}
          arrayKeys={[['contact_nm', 'contact_type', 'contact_value']]}
          title={`${elementSchema?.properties
            .dcsa_ebl_document.items.properties
            .contact_details.title}`}
        />

        {/* Consigment Items */}
        <Spacer />
        <div style={{ borderTop: "1px solid black" }}>
          {(consignment ?? [{}])?.map((consignment_item, index) => {
            return <>
              <MultiObjectField
                identifier="consignment"
                schema={elementSchema?.properties
                  .dcsa_ebl_document.items.properties}
                payload={consignment_item}
                arrayKeys={[['booking_ref', 'weight', 'weight_unit', 'volume', 'volume_unit', 'goods_description']]}
                title={<SubTitle2
                  title={`${elementSchema?.properties
                    .dcsa_ebl_document.items.properties
                    .consignment.title}  #${index + 1}`} />}
              />

              <Row>
                {/* HS Code */}
                <div style={{ flex: 1 }}>
                  <MultiValueField identifier="hscode" schema={elementSchema?.properties
                    .dcsa_ebl_document.items.properties
                    .consignment.items.properties}
                    payload={consignment_item}
                    escapePrintBottomBorder={true}
                  />
                </div>

                {/* Reference */}
                <MultiObjectField
                  identifier="references"
                  schema={elementSchema?.properties
                    .dcsa_ebl_document.items.properties
                    .consignment.items.properties}
                  payload={consignment_item?.references}
                  arrayKeys={[['type', 'value']]}
                  escapePrintBottomBorder={true}
                />
              </Row>

              {/* Custom Reference */}
              <MultiObjectField
                identifier="custom_references"
                schema={elementSchema?.properties
                  .dcsa_ebl_document.items.properties
                  .consignment.items.properties}
                payload={consignment_item?.custom_references}
                arrayKeys={[['type', 'country_cd', 'value']]}
              />

              {/* Cargo Items */}
              <IndentedTable>
                {(consignment_item?.cargo_items ?? [{}])?.map((cargo_item, index) => {
                  return <>
                    <MultiObjectField
                      identifier="cargo_items"
                      schema={elementSchema?.properties
                        .dcsa_ebl_document.items.properties
                        .consignment.items.properties}
                      payload={cargo_item}
                      arrayKeys={[['shipping_marks', 'equipment_ref', 'weight', 'weight_unit', 'volume', 'volume_unit']]}
                      title={<SubTitle3 title={`${elementSchema?.properties
                        .dcsa_ebl_document.items.properties
                        .consignment.items.properties
                        .cargo_items.title}  #${index + 1}`} />}
                    />

                    {/* Custom Reference */}
                    <MultiObjectField
                      identifier="customs_references"
                      schema={elementSchema?.properties
                        .dcsa_ebl_document.items.properties
                        .consignment.items.properties
                        .cargo_items.items.properties}
                      payload={cargo_item?.customs_references}
                      arrayKeys={[['type', 'country_cd', 'value']]}
                    />

                    {/* outer_packaging */}
                    <IndentedTable>
                      {(cargo_item?.outer_packaging ?? [{}])?.map((outer_packaging_item, index) => {
                        return <>
                          <MultiObjectField
                            identifier="outer_packaging"
                            schema={elementSchema?.properties
                              .dcsa_ebl_document.items.properties
                              .consignment.items.properties
                              .cargo_items.items.properties}
                            payload={outer_packaging_item}
                            arrayKeys={[['package_cd', 'package_no', 'description', 'imo_packaging_cd']]}
                            title={<SubTitle3 title={`${elementSchema?.properties
                              .dcsa_ebl_document.items.properties
                              .consignment.items.properties
                              .cargo_items.items.properties
                              .outer_packaging.title} #${index + 1}`} italic />}
                          />

                          {/* dangerous_goods */}
                          <IndentedTable>
                            {(outer_packaging_item?.dangerous_goods ?? [{}])?.map((dangerous_goods_item, index) => {
                              return <>
                                <MultiObjectField
                                  identifier="dangerous_goods"
                                  schema={elementSchema?.properties
                                    .dcsa_ebl_document.items.properties
                                    .consignment.items.properties
                                    .cargo_items.items.properties
                                    .outer_packaging.items.properties}
                                  payload={dangerous_goods_item}
                                  arrayKeys={[['code_variant_list', 'proper_shipping_nm', 'technical_nm', 'imo_class', 'subsidiary_risk1', 'subsidiary_risk2'], ['is_marine_pollutant', 'packing_group', 'is_limited_quantity', 'is_excepted_quantity', 'is_salvage_packings', 'is_empty_uncleaned_residue'], ['is_waste', 'is_hot', 'is_competent_authority_approval_provided', 'competent_authority_approval', 'ems_no', 'end_of_holding_time'], ['fumigation_dt', 'is_reportable_quantity', 'inhalation_zone', 'reference_standard_type', 'reference_number']]}
                                  title={<SubTitle3 title={`${elementSchema?.properties
                                    .dcsa_ebl_document.items.properties
                                    .consignment.items.properties
                                    .cargo_items.items.properties
                                    .outer_packaging.items.properties
                                    .dangerous_goods.title} #${index + 1}`} italic />}
                                />

                                {/* 'gross_weight', 'net_weight' */}
                                <Row>
                                  {/* Gross Weight */}
                                  <MultiObjectField
                                    style={{ flex: 1, display: "flex", flexDirection: "column", borderTop: "1px solid black", borderRight: "1px solid black" }}
                                    identifier="gross_weight"
                                    schema={elementSchema?.properties
                                      .dcsa_ebl_document.items.properties
                                      .consignment.items.properties
                                      .cargo_items.items.properties
                                      .outer_packaging.items.properties
                                      .dangerous_goods.items.properties}
                                    payload={dangerous_goods_item?.gross_weight}
                                    arrayKeys={[['value', 'unit']]}
                                  />

                                  {/* Net Weight */}
                                  <MultiObjectField
                                    style={{ flex: 1, display: 'flex', flexDirection: 'column', borderTop: "1px solid black" }}
                                    identifier="net_weight"
                                    schema={elementSchema?.properties
                                      .dcsa_ebl_document.items.properties
                                      .consignment.items.properties
                                      .cargo_items.items.properties
                                      .outer_packaging.items.properties
                                      .dangerous_goods.items.properties}
                                    payload={dangerous_goods_item?.net_weight}
                                    arrayKeys={[['value', 'unit']]}
                                  />
                                </Row>

                                {/* 'net_explosive_content', 'volume' */}
                                <Row>
                                  {/* Net Explosive Content */}
                                  <MultiObjectField
                                    style={{
                                      flex: 1,
                                      display: "flex",
                                      flexDirection: "column",
                                      borderTop: "1px solid black",
                                      borderRight: "1px solid black",
                                    }}
                                    identifier="net_explosive_content"
                                    schema={elementSchema?.properties
                                      .dcsa_ebl_document.items.properties
                                      .consignment.items.properties
                                      .cargo_items.items.properties
                                      .outer_packaging.items.properties
                                      .dangerous_goods.items.properties}
                                    payload={dangerous_goods_item?.net_explosive_content}
                                    arrayKeys={[['value', 'unit']]}
                                  />

                                  {/* Volume */}
                                  <MultiObjectField
                                    style={{ flex: 1, display: 'flex', flexDirection: 'column', borderTop: "1px solid black" }}
                                    identifier="volume"
                                    schema={elementSchema?.properties
                                      .dcsa_ebl_document.items.properties
                                      .consignment.items.properties
                                      .cargo_items.items.properties
                                      .outer_packaging.items.properties
                                      .dangerous_goods.items.properties}
                                    payload={dangerous_goods_item?.volume}
                                    arrayKeys={[['value', 'unit']]}
                                  />
                                </Row>

                                {/* 'inner_packaging', 'emergency_contact' */}
                                <Row>
                                  {/* Inner Packagings */}
                                  <MultiObjectField
                                    style={{ flex: 1, display: "flex", flexDirection: "column", borderTop: "1px solid black", borderRight: "1px solid black" }}
                                    identifier="inner_packaging"
                                    schema={elementSchema?.properties
                                      .dcsa_ebl_document.items.properties
                                      .consignment.items.properties
                                      .cargo_items.items.properties
                                      .outer_packaging.items.properties
                                      .dangerous_goods.items.properties}
                                    payload={dangerous_goods_item?.inner_packaging}
                                    arrayKeys={[['quantity', 'material', 'description']]}
                                    title={`${elementSchema?.properties
                                      .dcsa_ebl_document.items.properties
                                      .consignment.items.properties
                                      .cargo_items.items.properties
                                      .outer_packaging.items.properties
                                      .dangerous_goods.items.properties
                                      .inner_packaging.title}`}
                                  />

                                  {/* Emergency Contact Details */}
                                  <MultiObjectField
                                    style={{ flex: 1, display: 'flex', flexDirection: 'column', borderTop: "1px solid black" }}
                                    identifier="emergency_contact"
                                    schema={elementSchema?.properties
                                      .dcsa_ebl_document.items.properties
                                      .consignment.items.properties
                                      .cargo_items.items.properties
                                      .outer_packaging.items.properties
                                      .dangerous_goods.items.properties}
                                    payload={dangerous_goods_item?.emergency_contact}
                                    arrayKeys={[['contact', 'provider', 'phone', 'ref_no']]}
                                  />
                                </Row>

                                {/* Limits */}
                                <MultiObjectField
                                  style={{ flex: 1, display: 'flex', flexDirection: 'column', borderTop: "1px solid black" }}
                                  identifier="limits"
                                  schema={elementSchema?.properties
                                    .dcsa_ebl_document.items.properties
                                    .consignment.items.properties
                                    .cargo_items.items.properties
                                    .outer_packaging.items.properties
                                    .dangerous_goods.items.properties}
                                  payload={dangerous_goods_item?.limits}
                                  arrayKeys={[['temperature_unit', 'flash_point', 'transport_control_temperature', 'transport_emergency_temperature', 'sadt', 'sapt']]}
                                />
                              </>
                            })}
                          </IndentedTable>
                        </>
                      })}
                    </IndentedTable>
                  </>
                })}
              </IndentedTable>
            </>
          })}
        </div>

        {/* utilized_transport_equipment */}
        <Spacer />
        <div style={{ borderTop: "1px solid black" }}>
          {(utilized_transport_equipment ?? [{}])?.map((utilized_transport_equipment_item, index) => {
            return <>
              <MultiObjectField
                identifier="utilized_transport_equipment"
                schema={elementSchema?.properties
                  .dcsa_ebl_document.items.properties}
                payload={utilized_transport_equipment_item}
                arrayKeys={[['cargo_gross_weight', 'cargo_gross_weight_unit', 'cargo_gross_volume', 'cargo_gross_volume_unit', 'is_shipper_owned', 'is_non_operating_reefer']]}
                title={<SubTitle2
                  title={`${elementSchema?.properties
                    .dcsa_ebl_document.items.properties
                    .utilized_transport_equipment.title}  #${index + 1}`} />}
              />

              {/* 'equipment', 'seals' */}
              <Row>
                {/* equipment */}
                <MultiObjectField
                  style={{ flex: 1, display: "flex", flexDirection: "column", borderTop: "1px solid black", borderRight: "1px solid black" }}
                  identifier="equipment"
                  schema={elementSchema?.properties
                    .dcsa_ebl_document.items.properties
                    .utilized_transport_equipment.items.properties}
                  payload={utilized_transport_equipment_item?.equipment}
                  arrayKeys={[['equipment_ref', 'iso_equipment_cd', 'tare_weight', 'weight_unit']]}
                />

                {/* seals */}
                <MultiObjectField
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', borderTop: "1px solid black" }}
                  identifier="seals"
                  schema={elementSchema?.properties
                    .dcsa_ebl_document.items.properties
                    .utilized_transport_equipment.items.properties}
                  payload={utilized_transport_equipment_item?.seals}
                  arrayKeys={[['seal_number', 'seal_source', 'seal_type']]}
                />
              </Row>

              {/* 'references', 'customs_references' */}
              <Row>
                {/* references */}
                <MultiObjectField
                  style={{ flex: 1, display: "flex", flexDirection: "column", borderTop: "1px solid black", borderRight: "1px solid black" }}
                  identifier="references"
                  schema={elementSchema?.properties
                    .dcsa_ebl_document.items.properties
                    .utilized_transport_equipment.items.properties}
                  payload={utilized_transport_equipment_item?.references}
                  arrayKeys={[['type', 'value']]}
                />

                {/* customs_references */}
                <MultiObjectField
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', borderTop: "1px solid black" }}
                  identifier="customs_references"
                  schema={elementSchema?.properties
                    .dcsa_ebl_document.items.properties
                    .utilized_transport_equipment.items.properties}
                  payload={utilized_transport_equipment_item?.customs_references}
                  arrayKeys={[['type', 'country_cd', 'value']]}
                />
              </Row>

              {/* 'active_reefer' */}
              <IndentedTable>
                {/* active_reefer */}
                <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                  {(utilized_transport_equipment_item?.active_reefer ?? [{}])?.map((payload, index) => {
                    return <MultiObjectField
                      identifier="active_reefer"
                      schema={elementSchema?.properties
                        .dcsa_ebl_document.items.properties
                        .utilized_transport_equipment.items.properties}
                      payload={payload}
                      arrayKeys={[['temperature_setpoint', 'temperature_unit', 'o2_setpoint', 'co2_setpoint', 'humidity_setpoint', 'air_exchange_setpoint'], ['air_exchange_unit', 'is_ventilation_open', 'is_drain_holes_open', 'is_bulb_mode', 'is_cold_treatment_required', 'is_controlled_atmosphere_required']]}
                      title={`${elementSchema?.properties
                        .dcsa_ebl_document.items.properties
                        .utilized_transport_equipment.items.properties
                        .active_reefer
                        .title} #${index + 1}`}
                    />
                  })}
                </div>
              </IndentedTable>
            </>
          })}
        </div>

        {/* references / customs_references */}
        <Spacer />
        <Row>
          {/* references */}
          <MultiObjectField
            style={{ flex: 1, display: "flex", flexDirection: "column", borderTop: "1px solid black", borderRight: "1px solid black" }}
            identifier="references"
            schema={elementSchema?.properties
              .dcsa_ebl_document.items.properties}
            payload={references}
            arrayKeys={[['type', 'value']]}
          />

          {/* customs_references */}
          <MultiObjectField
            style={{ flex: 1, display: 'flex', flexDirection: 'column', borderTop: "1px solid black" }}
            identifier="customs_references"
            schema={elementSchema?.properties
              .dcsa_ebl_document.items.properties}
            payload={customs_references}
            arrayKeys={[['type', 'country_cd', 'value']]}
          />
        </Row>

        {/* document_party */}
        <Spacer />
        <div style={{ borderTop: "1px solid black" }}>
          {(document_party ?? [{}])?.map((document_party_item, index) => {
            return <>
              <MultiObjectField
                identifier="document_party"
                schema={elementSchema?.properties
                  .dcsa_ebl_document.items.properties}
                payload={document_party_item}
                arrayKeys={[['party_function', 'displayed_address', 'notified']]}
                title={<SubTitle2
                  title={`${elementSchema?.properties
                    .dcsa_ebl_document.items.properties
                    .document_party.title}  #${index + 1}`} />}
              />

              {/* Party */}
              <IndentedTable>
                {(document_party_item?.party ?? [{}])?.map((party_item, index) => {
                  return <>
                    {/* 'name', 'address', 'address_country', 'contact_nm', 'contact_type', 'contact_value' */}
                    <MultiObjectField
                      identifier="party"
                      schema={elementSchema?.properties
                        .dcsa_ebl_document.items.properties
                        .document_party.items.properties}
                      payload={party_item}
                      arrayKeys={[['name', 'address', 'address_country', 'contact_nm', 'contact_type', 'contact_value']]}
                      title={<SubTitle3 title={`${elementSchema?.properties
                        .dcsa_ebl_document.items.properties
                        .document_party.items.properties
                        .party.title}  #${index + 1}`} />}
                    />

                    {/* 'identifying_code', 'tax_ref' */}
                    <Row>
                      {/* identifying_code */}
                      <MultiObjectField
                        style={{ flex: 1, display: "flex", flexDirection: "column", borderTop: "1px solid black", borderRight: "1px solid black" }}
                        identifier="identifying_code"
                        schema={elementSchema?.properties
                          .dcsa_ebl_document.items.properties
                          .document_party.items.properties
                          .party
                          .items.properties}
                        payload={party_item?.identifying_code}
                        arrayKeys={[['party_cd', 'code_provider', 'code_name']]}
                      />

                      {/* tax_ref */}
                      <MultiObjectField
                        style={{ flex: 1, display: 'flex', flexDirection: 'column', borderTop: "1px solid black" }}
                        identifier="tax_ref"
                        schema={elementSchema?.properties
                          .dcsa_ebl_document.items.properties
                          .document_party.items.properties
                          .party
                          .items.properties}
                        payload={party_item?.tax_ref}
                        arrayKeys={[['type', 'country_code', 'value']]}
                      />
                    </Row>
                  </>
                })}
              </IndentedTable >
            </>
          })}
        </div>
      </div>
    ))}
  </>;
};

const Section2 = (document, elementSchema) => {
  const dcsa_ebl_issue_to = document?.dcsa_ebl_issue_to ?? [{
    platform_nm: "",
    legal_nm: "",
    registration_no: "",
    registration_loc: "",
    tax_ref: "",
    party_codes: [{
      party_code: "", code_provider: "", code_name: ""
    }]
  }];

  return (
    <div style={{ border: "2px solid black" }}>
      {(dcsa_ebl_issue_to ?? [{}])?.map((payload, index) => {
        return (
          <Row key={`dcsa_ebl_issue_to-${index}`}>
            <MultiObjectField
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                borderRight: "1px solid black",
              }}
              identifier="dcsa_ebl_issue_to"
              schema={elementSchema?.properties}
              payload={payload}
              arrayKeys={[['platform_nm', 'legal_nm', 'registration_no', 'registration_loc', 'tax_ref']]}
              title=""
            />

            <MultiObjectField
              identifier="party_codes"
              schema={elementSchema?.properties.dcsa_ebl_issue_to.items.properties}
              payload={payload?.party_codes}
              arrayKeys={[['party_code', 'code_provider', 'code_name']]}
            />
          </Row>
        );
      })}
    </div>
  );

};

const Section1 = (document, documentLogo) => {
  const { dcsa_ebl_jurisdiction, dcsa_ebl_seat } = document ??
  {
    dcsa_ebl_jurisdiction: "",
    dcsa_ebl_seat: "",
  };
  return (
    <div style={{ border: "1px solid black", borderRight: "2px solid" }}>
      <div style={{ display: "flex" }}>
        <div style={{ width: "33.3333%", border: "1px solid black" }}>
          <div style={{ padding: "8px", width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            {!documentLogo?.includes(".svg") && <img
              data-testid="logo"
              style={{ width: "75px", height: "auto" }}
              src={documentLogo ? `https://blob.sgtradex.io/${documentLogo}` : defaultSgtradexLogo}
              onError={(e) => { e.currentTarget.src = defaultSgtradexLogo; }}
              alt="fallback logo"
            />}
          </div>
        </div>
        <div style={{ width: "66.6667%" }}>
          <div style={{ display: "flex" }}>
            <div style={{ width: "66.6667%", border: "1px solid black" }}>
              <div style={{ padding: "8px" }}>
                <p style={{ fontWeight: 800 }}>
                  DCSA Electronic Bill of Lading
                </p>
                <p style={{ fontSize: "14px" }}>
                  Document issued by a carrier to a shipper that details the type, quantity, and destination of the goods being carried in DCSA standard
                </p>
              </div>
            </div>
            <div style={{ width: "33.3333%", border: "1px solid black", borderRight: "none" }}>
              <div style={{ padding: "8px", borderBottom: "2px solid black" }}>
                Jurisdiction
                <div style={{ fontWeight: 800, wordBreak: "break-word" }}>
                  {dcsa_ebl_jurisdiction}
                </div>
              </div>
              <div style={{ padding: "8px" }}>
                Seat
                <div style={{ fontWeight: 800, wordBreak: "break-word" }}>
                  {dcsa_ebl_seat}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const EBL_DCSATemplate = forwardRef(({ document, logo, endorsementChainLists, issuer, owner, holder, created, tokenRegistryVersion, tokenRegistryAddress, merkleRoot, elementSchema }, ref) => {
  const documentData = document;

  const documentDetailsRef = useRef();
  const endorsementChainRef = useRef();

  useImperativeHandle(ref, () => {
    return {
      handleClick: handleClickChild,
    };
  }, []);

  const handleClickChild = async (onComplete) => {
    console.log("PDF downloading started");
    let base64Image = "";
    if (logo.includes(".svg")) {
      await convertSvgUrlToBase64(logo).then((base64StringInternal) => base64Image = base64StringInternal);
    }
    const processPDFDownload = new ProcessPDF({
      staticHeader: (pdf) => pdfHeader(
        pdf,
        "E-Bill of Lading",
        merkleRoot
      ),
      orientation: "p",
      format: [794, 1123],
      marginX: 20,
      marginTop: 120,
      marginBottom: 60
    });

    const pdfSections = [
      { data: documentDetailsRef.current, type: "html" },
      endorsementChainLists && endorsementChainLists.length > 0 && { data: endorsementChainRef.current, type: "html" }
    ].filter(Boolean);

    await processPDFDownload.downloadPDF(pdfSections, base64Image, 900).then((response) => {
      console.log("response", response);
    }).catch((error) => {
      console.log("error", error);
    }).finally(() => {
      onComplete?.(); // Notify parent to hide loader
    });
  }

  return (<>
    {/* Document Details, Notice and Data Element Details */}
    <div ref={documentDetailsRef}>
      <div style={{ border: "1px solid #D7DAE3", borderRadius: "4px", backgroundColor: "#FFFFFF" }}>
        <div style={{ display: "flex", flexDirection: "column", padding: "16px" }}>
          <DocumentDetailsSection
            issuer={issuer}
            owner={owner}
            holder={holder}
            created={created}
            tokenRegistryVersion={tokenRegistryVersion}
            tokenRegistryAddress={tokenRegistryAddress}
          />
        </div>
      </div>
      <div style={{ border: "1px solid #D7DAE3", borderRadius: "4px", marginTop: "32px", backgroundColor: "#FFFFFF" }}>
        <div style={{ fontWeight: 800, fontSize: "12px", fontFamily: "Arial", borderBottom: "1px solid #D7DAE3", padding: "16px" }}>{"E-Bill of Lading"}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "32px", padding: "16px" }}>
          <div style={{ width: "600px", marginLeft: "auto", marginRight: "auto" }}>
            <div style={{ marginBottom: "32px" }}>{Section1(documentData, logo)}</div>
            <div style={{ textAlign: "center" }}>
              <p style={{ fontWeight: 800 }}>
                Issue To
              </p>
            </div>
            <div style={{ marginBottom: "32px" }}>{Section2(documentData, elementSchema)}</div>
            {Section3(documentData, elementSchema)}
          </div>
        </div>
      </div>
    </div>

    {/* Endorsement Chain */}
    <div ref={endorsementChainRef} style={{ border: "1px solid #D7DAE3", borderRadius: "4px", marginTop: "8px", backgroundColor: "#FFFFFF" }}>
      <div style={{ fontWeight: 800, fontSize: "12px", fontFamily: "Arial", borderBottom: "1px solid #D7DAE3", padding: "16px" }}>Endorsement Chain</div>
      <EndorsementChainSection endorsementChainLists={endorsementChainLists} />
    </div>
  </>);

});

EBL_DCSATemplate.propTypes = {
  document: PropTypes.object,
  elementSchema: PropTypes.object,
  logo: PropTypes.string,
  endorsementChainLists: PropTypes.array,
  issuer: PropTypes.string,
  owner: PropTypes.string,
  holder: PropTypes.string,
  created: PropTypes.string,
  tokenRegistryVersion: PropTypes.string,
  tokenRegistryAddress: PropTypes.string,
  merkleRoot: PropTypes.string,
};

SubTitle1.propTypes = {
  title: PropTypes.string,
};

SubTitle2.propTypes = {
  title: PropTypes.string,
};

SubTitle3.propTypes = {
  title: PropTypes.string,
  italic: PropTypes.bool,
};

SingleValueField.propTypes = {
  identifier: PropTypes.string,
  schema: PropTypes.object,
  payload: PropTypes.object,
  index: PropTypes.number,
};

Row.propTypes = {
  children: PropTypes.node,
  escapePrintBottomBorder: PropTypes.bool
}

MultiValueField.propTypes = {
  identifier: PropTypes.string,
  schema: PropTypes.object,
  payload: PropTypes.object,
  index: PropTypes.number,
  escapePrintBottomBorder: PropTypes.bool
}

MultiObjectField.propTypes = {
  identifier: PropTypes.string,
  schema: PropTypes.object,
  payload: PropTypes.object,
  arrayKeys: PropTypes.array,
  title: PropTypes.string,
  escapePrintBottomBorder: PropTypes.bool
}

IndentedTable.propTypes = {
  children: PropTypes.node,
}